import { Button, Checkbox, token } from "@may-i/mash-design-system";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface DeleteLineZoneTarget {
  type: "line" | "zone";
  cameraId: number;
  id: number;
  key: string;
  hasAnalysisConfig: boolean;
  hasFloorPlanConfig: boolean;
}

interface DeleteLineZoneDialogProps {
  target: DeleteLineZoneTarget | null;
  onConfirm: (target: DeleteLineZoneTarget, deleteSnapshot: boolean, deleteFloorPlan: boolean) => void;
  onCancel: () => void;
}

export function DeleteLineZoneDialog({ target, onConfirm, onCancel }: DeleteLineZoneDialogProps) {
  const { t } = useTranslation("analysis-draft");
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [deleteSnapshot, setDeleteSnapshot] = useState(true);
  const [deleteFloorPlan, setDeleteFloorPlan] = useState(true);

  // Reset checkboxes when target changes
  useEffect(() => {
    if (target) {
      setDeleteSnapshot(target.hasAnalysisConfig);
      setDeleteFloorPlan(target.hasFloorPlanConfig);
    }
  }, [target]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) {
      dialog.showModal();
    }
  }, [target]);

  if (!target) return null;

  const isLine = target.type === "line";
  const title = isLine ? t("delete.lineTitle") : t("delete.zoneTitle");
  const typeLabel = isLine ? t("delete.lineLabel") : t("delete.zoneLabel");

  const canConfirm = deleteSnapshot || deleteFloorPlan;

  return (
    <StyledDialog
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <Header>{title}</Header>
      <Body>
        <Message>
          {t("delete.messagePre")}'<BoldKey>{target.key}</BoldKey>' {typeLabel}{t("delete.messageSuf")}
        </Message>
        <CheckboxRow>
          <Checkbox
            size="medium"
            color="primary"
            label={t("delete.snapshot")}
            checked={deleteSnapshot}
            onCheckedChange={(v) => typeof v === "boolean" && setDeleteSnapshot(v)}
            disabled={!target.hasAnalysisConfig}
          />
          <Checkbox
            size="medium"
            color="primary"
            label={t("delete.floorPlan")}
            checked={deleteFloorPlan}
            onCheckedChange={(v) => typeof v === "boolean" && setDeleteFloorPlan(v)}
            disabled={!target.hasFloorPlanConfig}
          />
        </CheckboxRow>
      </Body>
      <Footer>
        <Button variant="outlined" color="inherit" size="medium" onClick={onCancel}>
          {t("delete.cancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          size="medium"
          disabled={!canConfirm}
          onClick={() => onConfirm(target, deleteSnapshot, deleteFloorPlan)}
        >
          {t("delete.confirm")}
        </Button>
      </Footer>
    </StyledDialog>
  );
}

const StyledDialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: 480px;
  height: fit-content;
  margin: auto;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);

  &::backdrop {
    background: rgba(17, 17, 31, 0.32);
  }
`;

const Header = styled.div`
  padding: 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
`;

const Message = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const BoldKey = styled.span`
  font-weight: 600;
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 24px;
`;
